import "@/components/workspace/workspace.css";
import {
	Workspace,
	type WorkspaceConfig,
	useWorkspaceContext,
} from "@/components/workspace";
import { useGlobalCommerceShortcuts } from "@/hooks/use-commerce-shortcuts";
import { useWsStore } from "@/lib/ws";
import { ModuleErrorBoundary } from "@/components/ModuleErrorBoundary";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/commerce")({
	component: CommerceLayout,
});

// ── Real-time event toasts ─────────────────────────────────────────

const COMMERCE_TOAST_ENTITIES: Record<string, string> = {
	sales_order: "Заказ",
	payment_transaction: "Платёж",
	shipment: "Отгрузка",
	checkout_session: "Оформление",
	quote: "КП",
};

const COMMERCE_TOAST_EVENTS: Record<string, string> = {
	Created: "создан",
	Updated: "обновлён",
	Confirmed: "подтверждён",
	StatusChanged: "статус изменён",
	Completed: "завершён",
	Captured: "списан",
	Refunded: "возвращён",
	SessionCreated: "создана сессия",
	Initiated: "начато",
};

function useCommerceToasts() {
	useEffect(() => {
		const unsub = useWsStore.getState().subscribe((evt) => {
			const entityLabel = COMMERCE_TOAST_ENTITIES[evt.entity_type];
			if (!entityLabel) return;

			const eventType = evt.action.split(".")[1] ?? "";
			const eventLabel = COMMERCE_TOAST_EVENTS[eventType];
			if (!eventLabel) return;

			toast.info(`${entityLabel} ${eventLabel}`, {
				duration: 3000,
			});
		});
		return unsub;
	}, []);
}

// ── Inner layout (uses workspace context) ───────────────────────────

function CommerceWorkspaceInner() {
	const { statusBar } = useWorkspaceContext();

	useCommerceToasts();
	useGlobalCommerceShortcuts();

	const config = useMemo<WorkspaceConfig>(
		() => ({ menuBar: [], statusBar }),
		[statusBar],
	);

	return (
		<Workspace config={config}>
			<ModuleErrorBoundary>
				<Outlet />
			</ModuleErrorBoundary>
		</Workspace>
	);
}

// ── Main layout ─────────────────────────────────────────────────────

function CommerceLayout() {
	return <CommerceWorkspaceInner />;
}
